import React, { useState } from 'react';
import { DollarSign, TrendingUp, AlertCircle, HeartHandshake, FileText } from 'lucide-react';
import { Card, Button } from '../ui';

export function PhilanthropyProfitMarginPredictor() {
  const [ticketPrice, setTicketPrice] = useState<number>(25);
  const [expectedTurnout, setExpectedTurnout] = useState<number>(100);
  const [fixedCosts, setFixedCosts] = useState<number>(500); // Venue, marketing, etc.
  const [costPerAttendee, setCostPerAttendee] = useState<number>(5); // Food, swag, etc.

  // Math
  const grossRevenue = ticketPrice * expectedTurnout;
  const totalVariableCosts = costPerAttendee * expectedTurnout;
  const totalExpenses = fixedCosts + totalVariableCosts;
  const netProfit = grossRevenue - totalExpenses;
  
  // Break even calculation: Fixed Costs / (Ticket Price - Variable Cost Per Attendee)
  const marginPerTicket = ticketPrice - costPerAttendee;
  const breakEvenTurnout = marginPerTicket > 0 ? Math.ceil(fixedCosts / marginPerTicket) : 'Impossible';
  
  const profitMarginPercent = grossRevenue > 0 ? ((netProfit / grossRevenue) * 100).toFixed(1) : 0;
  const isLoss = netProfit < 0;

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-700 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center gap-8 border-b-8 border-rose-900">
        <div className="absolute top-0 right-0 p-8 opacity-20 pointer-events-none">
          <HeartHandshake className="w-64 h-64" />
        </div>
        <div className="flex-1 z-10">
          <div className="flex items-center gap-2 text-pink-200 font-bold tracking-widest uppercase mb-4 text-sm">
            <TrendingUp className="w-4 h-4" /> Nonprofits & Greek Life
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
            Philanthropy Margin Predictor
          </h2>
          <p className="text-pink-100 max-w-2xl text-lg font-medium">
            Don't accidentally lose money on your next fundraiser. Map out your fixed and variable costs to find your exact break-even point before you sell a single ticket.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        
        {/* Input Column */}
        <div className="md:col-span-4 space-y-6">
          <Card className="p-6 bg-white dark:bg-slate-900 shadow-xl border-t-4 border-t-pink-500">
            <h3 className="font-bold text-xl mb-6 flex items-center text-slate-800 dark:text-white">
              <DollarSign className="w-5 h-5 mr-2 text-pink-600" /> Event Assumptions
            </h3>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Ticket Price ($)</label>
                <input
                  type="number"
                  value={ticketPrice}
                  onChange={(e) => setTicketPrice(Number(e.target.value))}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-pink-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Expected Turnout (People)</label>
                <input
                  type="number"
                  value={expectedTurnout}
                  onChange={(e) => setExpectedTurnout(Number(e.target.value))}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-pink-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>

              <div className="pt-4 border-t dark:border-slate-800">
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Fixed Costs ($)</label>
                <p className="text-xs text-slate-500 mb-2">Venue rental, DJ, permits, marketing. Things that don't change based on attendance.</p>
                <input
                  type="number"
                  value={fixedCosts}
                  onChange={(e) => setFixedCosts(Number(e.target.value))}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-pink-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Variable Cost Per Attendee ($)</label>
                <p className="text-xs text-slate-500 mb-2">Food, plates, t-shirts, drinks per person.</p>
                <input
                  type="number"
                  value={costPerAttendee}
                  onChange={(e) => setCostPerAttendee(Number(e.target.value))}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-pink-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Results Column */}
        <div className="md:col-span-8 space-y-6">
          <Card className="p-8 bg-slate-50 dark:bg-slate-900 border-2 shadow-inner h-full flex flex-col">
            <div className="flex justify-between items-start mb-8 border-b pb-6 dark:border-slate-800">
              <div>
                <h3 className="text-2xl font-black text-slate-800 dark:text-white">Fundraising Projection</h3>
                <p className="text-slate-500 mt-1">Based on selling {expectedTurnout} tickets at ${ticketPrice} each.</p>
              </div>
              <Button variant="secondary" onClick={() => window.print()} className="border-pink-600 text-pink-700 hover:bg-pink-50 hidden sm:flex">
                <FileText className="w-4 h-4 mr-2" /> Export PDF
              </Button>
            </div>

            {/* The Big Number */}
            <div className={`p-8 rounded-2xl border-2 shadow-sm mb-8 text-center transition-colors ${isLoss ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'}`}>
              <div className="text-slate-500 font-bold uppercase tracking-widest text-sm mb-2">
                {isLoss ? 'Projected Loss' : 'Actual Donation Raised (Net Profit)'}
              </div>
              <div className={`text-6xl font-black ${isLoss ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                ${Math.abs(netProfit).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
              <div className={`mt-2 font-bold ${isLoss ? 'text-red-500' : 'text-emerald-600'}`}>
                {profitMarginPercent}% Profit Margin
              </div>
            </div>

            {/* Breakdown Grid */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8 flex-1">
              <div className="bg-white dark:bg-slate-950 p-6 rounded-xl border dark:border-slate-800">
                <div className="text-slate-500 text-sm font-bold mb-1">Gross Revenue</div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">${grossRevenue.toLocaleString()}</div>
              </div>
              
              <div className="bg-white dark:bg-slate-950 p-6 rounded-xl border dark:border-slate-800">
                <div className="text-slate-500 text-sm font-bold mb-1">Total Expenses</div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">${totalExpenses.toLocaleString()}</div>
              </div>

              <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700">
                <div className="text-slate-600 dark:text-slate-300 text-sm font-bold mb-1">Break-Even Point</div>
                <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400">
                  {breakEvenTurnout} <span className="text-sm font-bold text-slate-500">Tickets</span>
                </div>
              </div>
            </div>

            {marginPerTicket <= 0 && (
              <div className="bg-red-100 border border-red-300 text-red-800 p-4 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-bold">Mathematically Impossible to Profit</h4>
                  <p className="text-sm">Your variable cost per attendee (${costPerAttendee}) is higher than or equal to your ticket price (${ticketPrice}). You will lose money on every single person who shows up. You must raise the ticket price or lower food/swag costs.</p>
                </div>
              </div>
            )}

          </Card>
        </div>

      </div>
    </div>
  );
}
